import { z } from 'zod';
/**
 * Contrat d'entrée du webhook `POST /api/v1/webhooks/wazuh`.
 *
 * Frontière d'intégration avec le nœud Proxmox client : le script
 * `custom-minisoc.py` déclaré comme integration Wazuh sérialise l'alerte
 * générée par le Manager et la POSTe au service central à travers le tunnel VPN
 * à double chiffrement (§5.1 étape 4).
 *
 * Règles de conception :
 * - Enveloppe versionnée (`schemaVersion`) : les nœuds sont mis à jour par
 *   Ansible et peuvent temporairement être en retard d'une version.
 * - `passthrough()` sur les blocs Wazuh natifs : leur forme varie selon le
 *   decoder (Windows EventChannel, auditd, ClamAV). On valide ce dont on a
 *   besoin et on conserve le reste tel quel pour l'affichage du log brut.
 * - Aucune donnée nominative n'est requise : les champs utilisateur restent
 *   optionnels, un nœud peut les avoir filtrés.
 */
/** Bloc `rule` de l'alerte Wazuh. */
export const wazuhRuleSchema = z
    .object({
    id: z.string().min(1),
    level: z.number().int().min(0).max(16),
    description: z.string().min(1),
    firedtimes: z.number().int().nonnegative().optional(),
    groups: z.array(z.string()).optional(),
    /**
     * Métadonnées MITRE propagées depuis la règle SIGMA source par la
     * transcompilation (§5.3.1 étape 4).
     */
    mitre: z
        .object({
        id: z.array(z.string()).optional(),
        tactic: z.array(z.string()).optional(),
        technique: z.array(z.string()).optional(),
    })
        .optional(),
})
    .passthrough();
/** Bloc `agent` : endpoint à l'origine de l'événement. */
export const wazuhAgentSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    ip: z.string().optional(),
})
    .passthrough();
export const wazuhAlertSchema = z
    .object({
    /** Identifiant Wazuh de l'alerte, forme `<epoch>.<offset>`. Sert de clé d'idempotence. */
    id: z.string().min(1),
    /** ISO 8601 tel que produit par Wazuh. */
    timestamp: z.string().min(1),
    rule: wazuhRuleSchema,
    agent: wazuhAgentSchema,
    manager: z.object({ name: z.string() }).passthrough().optional(),
    decoder: z.object({ name: z.string().optional() }).passthrough().optional(),
    location: z.string().optional(),
    /** Ligne de log d'origine, non normalisée. */
    full_log: z.string().optional(),
    /** Champs décodés — forme dépendante du decoder. */
    data: z.record(z.unknown()).optional(),
    /** Métadonnées syscheck (FIM) quand la règle en provient. */
    syscheck: z.record(z.unknown()).optional(),
})
    .passthrough();
export const wazuhWebhookPayloadSchema = z.object({
    schemaVersion: z.literal(1),
    /**
     * Nom normalisé du nœud émetteur (§2.1), ex. `C001-73200-01-SRV-01`.
     * Doit correspondre à un `Appliance.code` connu, sinon l'alerte est rejetée.
     */
    applianceCode: z.string().min(3).max(64),
    /** Horodatage d'émission du nœud, couvert par la signature HMAC (anti-replay). */
    sentAt: z.string().datetime({ offset: true }),
    alert: wazuhAlertSchema,
});
/** En-têtes de signature attendus sur le webhook. */
export const WAZUH_WEBHOOK_HEADERS = {
    /** `sha256=<hex>` — HMAC du corps brut avec le secret du nœud. */
    signature: 'x-minisoc-signature',
    /** Epoch en secondes, rejoué dans le calcul HMAC. */
    timestamp: 'x-minisoc-timestamp',
    /** Code du nœud, redondant avec le corps mais nécessaire avant parsing. */
    appliance: 'x-minisoc-appliance',
};
/**
 * Extrait un identifiant de compte lisible depuis les champs décodés Wazuh.
 *
 * Windows EventChannel place l'information à des endroits différents selon
 * l'Event ID : `targetUserName` pour un 4624, `memberName` pour un 4732,
 * `subjectUserName` pour l'auteur de l'action. auditd expose `auid`/`acct`.
 * On teste les emplacements par ordre de pertinence métier.
 */
export function extractSubjectUser(alert) {
    const eventdata = readNested(alert.data, ['win', 'eventdata']);
    const audit = readNested(alert.data, ['audit']);
    const candidates = [
        readString(eventdata, 'memberName'),
        readString(eventdata, 'targetUserName'),
        readString(eventdata, 'subjectUserName'),
        readString(audit, 'acct'),
        readString(alert.data, 'srcuser'),
        readString(alert.data, 'dstuser'),
    ];
    return candidates.find((value) => value !== undefined && value !== '-' && value.length > 0);
}
/** Extrait l'IP source quand la règle en fournit une (brute force, connexion distante). */
export function extractSourceIp(alert) {
    const eventdata = readNested(alert.data, ['win', 'eventdata']);
    const candidates = [
        readString(eventdata, 'ipAddress'),
        readString(alert.data, 'srcip'),
        readString(alert.data, 'src_ip'),
    ];
    return candidates.find((value) => value !== undefined && value !== '-' && value !== '::1');
}
/** Extrait l'Event ID Windows, utile pour l'affichage et le recoupement. */
export function extractWindowsEventId(alert) {
    const system = readNested(alert.data, ['win', 'system']);
    return readString(system, 'eventID') ?? readString(system, 'eventRecordID');
}
function readNested(source, path) {
    let current = source;
    for (const key of path) {
        if (!isRecord(current))
            return undefined;
        current = current[key];
    }
    return isRecord(current) ? current : undefined;
}
function readString(source, key) {
    const value = source?.[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=wazuh.js.map